import { useEffect, useRef, useState } from "react";
import { NavLink, Route, Routes, Link, useNavigate, useParams } from "react-router-dom";
import { api, formatPrice, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, ShoppingBag, Plus, Trash2, Pencil, Upload, X, ArrowLeft, Save, Star, MessageCircle, Copy,
  ArrowUp, ArrowDown, ArrowUpDown, Users,
} from "lucide-react";

function makeSlug(value) {
  return (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

const DEFAULT_HEX = "#B7B7B7";

function normalizeHexColor(value, fallback = DEFAULT_HEX) {
  const raw = String(value || "").trim();
  if (!raw) return fallback;

  let hex = raw.startsWith("#") ? raw.slice(1) : raw;
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    hex = hex.split("").map((ch) => ch + ch).join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
    return fallback;
  }
  return `#${hex.toUpperCase()}`;
}

const STATUS_LABEL = {
  new: { label: "Новый", cls: "bg-accent/10 text-accent border-accent/30" },
  processing: { label: "В обработке", cls: "bg-warning/10 text-warning border-warning/30" },
  delivered: { label: "Доставлен", cls: "bg-success/10 text-success border-success/30" },
  cancelled: { label: "Отменён", cls: "bg-danger/10 text-danger border-danger/30" },
};

function StatCard({ label, value, accent = "text-ink" }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-6">
      <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">{label}</div>
      <div className={`mt-3 text-3xl font-extrabold tracking-tightest ${accent}`}>{value}</div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return <div className="text-sm text-muted">Загрузка…</div>;
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Заказов всего" value={stats.total_orders} />
        <StatCard label="Новых" value={stats.new_orders} accent="text-accent" />
        <StatCard label="В обработке" value={stats.processing} accent="text-warning" />
        <StatCard label="Доставлено" value={stats.delivered} accent="text-success" />
        <StatCard label="Пользователей" value={stats.users_count} />
        <StatCard label="Товаров" value={stats.products_count} />
        <StatCard label="Быстрых заявок" value={stats.total_quick_orders} />
        <StatCard label="Заявок с UTM-меткой" value={`${stats.leads_with_utm} / ${stats.leads_total}`} />
        <div className="rounded-2xl border border-line bg-ink p-6 text-white lg:col-span-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/60">Выручка (обработанные + доставленные)</div>
          <div className="mt-3 text-4xl font-extrabold tracking-tightest">{formatPrice(stats.revenue)}</div>
        </div>
      </div>
      <div className="rounded-2xl border border-line bg-white p-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Источники трафика (UTM)</div>
        <div className="mt-3 rounded-xl bg-paper p-4 text-xs leading-relaxed text-muted">
          <p className="font-semibold text-ink">Как пользоваться</p>
          <p className="mt-1">
            Добавьте метки к ссылке на сайт при запуске рекламы или рассылки — сайт сам их запомнит у посетителя:
          </p>
          <p className="mt-2 break-all rounded-lg bg-white px-3 py-2 font-mono text-[11px] text-ink">
            https://vip77.store/?utm_source=vk&utm_medium=cpc&utm_campaign=avgust
          </p>
          <p className="mt-2">
            Поддерживаются <b>utm_source</b> (площадка: vk, yandex, telegram…), <b>utm_medium</b> (тип: cpc, email, social…),
            {" "}<b>utm_campaign</b> (название кампании), а также необязательные <b>utm_term</b> и <b>utm_content</b>.
          </p>
          <p className="mt-2">
            Метка запоминается на устройстве посетителя и подставится в заявку, даже если он оформит заказ через несколько
            страниц. Разбивку по источникам смотрите ниже, а по каждой заявке — в разделах «Заказы» и «Быстрые заявки»
            (колонка UTM).
          </p>
        </div>
        {stats.utm_sources && stats.utm_sources.length > 0 ? (
          <ul className="mt-4 space-y-2">
            {stats.utm_sources.map((row) => (
              <li key={row.source} className="flex items-center justify-between gap-3 text-sm">
                <span className="font-semibold text-ink">{row.source}</span>
                <span className="text-muted">{row.count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-muted">Пока нет заявок с UTM-метками.</p>
        )}
      </div>
    </div>
  );
}

function UtmBadge({ utm }) {
  if (!utm || !utm.utm_source) return <span className="text-xs text-muted">—</span>;
  return (
    <div className="text-xs">
      <div className="font-semibold text-ink">{utm.utm_source}</div>
      {utm.utm_campaign && <div className="text-muted">{utm.utm_campaign}</div>}
    </div>
  );
}

function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    const params = { page, page_size: 10 };
    if (filter) params.status = filter;
    const { data } = await api.get("/admin/orders", { params });
    setOrders(data.items || []);
    setPages(data.pages || 1);
    setTotal(data.total || 0);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page]);
  useEffect(() => { setPage(1); }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success("Статус обновлён");
      load();
    } catch {
      toast.error("Не удалось обновить статус");
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[["", "Все"], ["new", "Новые"], ["processing", "В обработке"], ["delivered", "Доставлены"], ["cancelled", "Отменены"]].map(([v, label]) => (
            <button
              key={v || "all"}
              data-testid={`orders-filter-${v || "all"}`}
              onClick={() => setFilter(v)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                filter === v ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="text-xs text-muted">Всего: {total}</div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">
              <th className="p-4">Заказ</th>
              <th className="p-4">Клиент</th>
              <th className="p-4">Товары</th>
              <th className="p-4">Сумма</th>
              <th className="p-4">UTM</th>
              <th className="p-4">Статус</th>
              <th className="p-4">Действие</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const s = STATUS_LABEL[o.status] || STATUS_LABEL.new;
              return (
                <tr key={o.id} data-testid={`admin-order-${o.order_no}`} className="border-b border-line align-top">
                  <td className="p-4">
                    <div className="font-bold text-ink">{o.order_no}</div>
                    <div className="text-xs text-muted">{new Date(o.created_at).toLocaleString("ru-RU")}</div>
                  </td>
                  <td className="p-4">
                    <div className="font-semibold text-ink">{o.customer_name}</div>
                    <div className="text-xs text-muted">{o.phone}</div>
                    <div className="text-xs text-muted">{o.address}</div>
                  </td>
                  <td className="p-4">
                    <ul className="space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="text-xs text-ink">{it.product_name} · {it.variation_label} × {it.quantity}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="p-4 font-bold text-ink">{formatPrice(o.total)}</td>
                  <td className="p-4"><UtmBadge utm={o.utm} /></td>
                  <td className="p-4"><span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${s.cls}`}>{s.label}</span></td>
                  <td className="p-4">
                    <select
                      data-testid={`admin-order-status-${o.order_no}`}
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs outline-none focus:border-ink"
                    >
                      <option value="new">Новый</option>
                      <option value="processing">В обработке</option>
                      <option value="delivered">Доставлен</option>
                      <option value="cancelled">Отменён</option>
                    </select>
                  </td>
                </tr>
              );
            })}
            {orders.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-sm text-muted">Заказов пока нет</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2" data-testid="orders-pagination">
          <button
            data-testid="orders-page-prev"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            ← Назад
          </button>
          <div className="px-3 text-sm font-semibold text-ink">
            {page} / {pages}
          </div>
          <button
            data-testid="orders-page-next"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
}

function QuickOrdersAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    const params = { page, page_size: 20 };
    if (filter) params.status = filter;
    const { data } = await api.get("/admin/quick-orders", { params });
    setItems(data.items || []);
    setPages(data.pages || 1);
    setTotal(data.total || 0);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page]);
  useEffect(() => { setPage(1); }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quick-orders/${id}/status`, { status });
      toast.success("Статус обновлён");
      load();
    } catch {
      toast.error("Не удалось обновить статус");
    }
  };

  return (
    <div data-testid="admin-quick-orders">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[["", "Все"], ["new", "Новые"], ["processing", "В обработке"], ["delivered", "Доставлены"], ["cancelled", "Отменены"]].map(([v, label]) => (
            <button
              key={v || "all"}
              onClick={() => setFilter(v)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                filter === v ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="text-xs text-muted">Всего: {total}</div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">
              <th className="p-4">Дата</th>
              <th className="p-4">Клиент</th>
              <th className="p-4">Товар</th>
              <th className="p-4">Страница</th>
              <th className="p-4">UTM</th>
              <th className="p-4">Статус</th>
              <th className="p-4">Действие</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => {
              const s = STATUS_LABEL[o.status] || STATUS_LABEL.new;
              return (
                <tr key={o.id} className="border-b border-line align-top">
                  <td className="p-4 text-xs text-muted">{new Date(o.created_at).toLocaleString("ru-RU")}</td>
                  <td className="p-4">
                    <div className="font-semibold text-ink">{o.customer_name}</div>
                    <div className="text-xs text-muted">{o.phone}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-xs text-ink">{o.product_name || "—"}</div>
                    <div className="text-xs text-muted">{o.variation_label}</div>
                  </td>
                  <td className="p-4 text-xs text-muted">{o.source_page || "—"}</td>
                  <td className="p-4"><UtmBadge utm={o.utm} /></td>
                  <td className="p-4"><span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${s.cls}`}>{s.label}</span></td>
                  <td className="p-4">
                    <select
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs outline-none focus:border-ink"
                    >
                      <option value="new">Новый</option>
                      <option value="processing">В обработке</option>
                      <option value="delivered">Доставлен</option>
                      <option value="cancelled">Отменён</option>
                    </select>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-sm text-muted">Заявок пока нет</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            ← Назад
          </button>
          <div className="px-3 text-sm font-semibold text-ink">
            {page} / {pages}
          </div>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    const params = { page, page_size: 20 };
    if (q) params.q = q;
    const { data } = await api.get("/admin/users", { params });
    setUsers(data.items || []);
    setPages(data.pages || 1);
    setTotal(data.total || 0);
  };

  const loadStats = async () => {
    const { data } = await api.get("/admin/users/stats");
    setStats(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, page]);
  useEffect(() => { setPage(1); }, [q]);
  useEffect(() => { loadStats(); }, []);

  return (
    <div data-testid="admin-users">
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-2xl border border-line bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">Всего пользователей</div>
            <div className="mt-1 text-2xl font-extrabold text-ink">{stats.total_users}</div>
          </div>
          <div className="rounded-2xl border border-line bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">Новых за 7 дней</div>
            <div className="mt-1 text-2xl font-extrabold text-ink">{stats.new_last_7d}</div>
          </div>
          <div className="rounded-2xl border border-line bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">Новых за 30 дней</div>
            <div className="mt-1 text-2xl font-extrabold text-ink">{stats.new_last_30d}</div>
          </div>
          <div className="rounded-2xl border border-line bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">С заказами</div>
            <div className="mt-1 text-2xl font-extrabold text-ink">{stats.users_with_orders} <span className="text-sm font-semibold text-muted">({stats.conversion_rate}%)</span></div>
          </div>
        </div>
      )}
      {stats?.top_spenders?.length > 0 && (
        <div className="mb-6 rounded-2xl border border-line bg-white p-4">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">Топ покупателей</div>
          <ul className="space-y-2">
            {stats.top_spenders.map((s) => (
              <li key={s.user_id} className="flex items-center justify-between text-sm">
                <span className="text-ink">{s.name || "—"} <span className="text-muted">({s.email})</span></span>
                <span className="font-bold text-ink">{formatPrice(s.total)} <span className="font-normal text-muted">· {s.orders_count} зак.</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mb-4 flex items-center justify-between gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Поиск по имени, email или телефону"
          className="w-full max-w-sm rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
        />
        <div className="text-xs text-muted">Всего: {total}</div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">
              <th className="p-4">Имя</th>
              <th className="p-4">Контакты</th>
              <th className="p-4">Регистрация</th>
              <th className="p-4">Заказы</th>
              <th className="p-4">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-line align-top">
                <td className="p-4">
                  <div className="font-semibold text-ink">{u.name}</div>
                  {u.role === "admin" && <div className="text-xs text-accent">Админ</div>}
                </td>
                <td className="p-4">
                  <div className="text-xs text-ink">{u.email}</div>
                  <div className="text-xs text-muted">{u.phone}</div>
                </td>
                <td className="p-4 text-xs text-muted">{new Date(u.created_at).toLocaleDateString("ru-RU")}</td>
                <td className="p-4 text-ink">{u.orders_count}</td>
                <td className="p-4 font-bold text-ink">{formatPrice(u.orders_total)}</td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-sm text-muted">Пользователей не найдено</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            ← Назад
          </button>
          <div className="px-3 text-sm font-semibold text-ink">
            {page} / {pages}
          </div>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
}

function PriceSyncAdmin() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkStatus, setBulkStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [runningId, setRunningId] = useState(null);

  const load = async () => {
    const params = { page, page_size: 20 };
    if (filter === "on") params.enabled = true;
    if (filter === "off") params.enabled = false;
    if (filter === "error") params.error_only = true;
    if (filter === "hidden") params.hidden_only = true;
    if (categoryFilter) params.category_slug = categoryFilter;
    params.sort_by = sortBy;
    params.sort_dir = sortDir;
    const [{ data: list }, { data: statsData }] = await Promise.all([
      api.get("/admin/price-sync/products", { params }),
      api.get("/admin/price-sync/stats"),
    ]);
    setItems(list.items || []);
    setPages(list.pages || 1);
    setTotal(list.total || 0);
    setStats(statsData);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, categoryFilter, sortBy, sortDir, page]);
  useEffect(() => { setPage(1); }, [filter, categoryFilter, sortBy, sortDir]);

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir(column === "last_sync" ? "desc" : "asc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown size={12} className="text-muted-soft" />;
    return sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };
  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const pollBulkStatus = async () => {
    const { data } = await api.get("/admin/price-sync/bulk-status");
    setBulkStatus(data);
    return data;
  };

  useEffect(() => {
    pollBulkStatus();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!bulkStatus?.running) return;
    const timer = setInterval(async () => {
      const data = await pollBulkStatus();
      if (!data.running) {
        clearInterval(timer);
        toast.success(`Синхронизация завершена: ${data.done}/${data.total}, изменено цен: ${data.changed || 0}`);
        load();
      }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [bulkStatus?.running]);

  const toggle = async (id, enabled) => {
    try {
      await api.patch(`/admin/products/${id}/price-sync`, { enabled });
      toast.success(enabled ? "Синхронизация включена" : "Синхронизация выключена");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось изменить настройку");
    }
  };

  const runNow = async (id) => {
    setRunningId(id);
    try {
      await api.post(`/admin/products/${id}/price-sync/run`);
      toast.success("Цена обновлена");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось обновить цену");
    } finally {
      setRunningId(null);
    }
  };

  const runBulk = async () => {
    try {
      await api.post("/admin/price-sync/run-bulk", null, { params: bulkCategory ? { category_slug: bulkCategory } : {} });
      toast.success(bulkCategory ? "Синхронизация категории запущена" : "Синхронизация всех товаров запущена");
      pollBulkStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось запустить синхронизацию");
    }
  };

  return (
    <div data-testid="admin-price-sync">
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard label="Товаров со ссылкой" value={stats.products_with_link} />
          <StatCard label="Синхронизация включена" value={stats.sync_enabled} accent="text-accent" />
          <StatCard label="Обновлено за 24ч" value={stats.synced_last_24h} accent="text-success" />
          <StatCard label="Ошибок" value={stats.errors} accent={stats.errors > 0 ? "text-danger" : "text-ink"} />
          <button type="button" onClick={() => setFilter("hidden")} className="w-full text-left">
            <StatCard label="Скрыто (нет у поставщика)" value={stats.hidden} accent={stats.hidden > 0 ? "text-danger" : "text-ink"} />
          </button>
        </div>
      )}
      <p className="mb-4 text-xs text-muted">
        Раз в {stats?.interval_minutes || 15} мин обновляется до {stats?.batch_size || 8} товаров с истёкшим 24-часовым сроком — так конкурента не сканируют пачкой сразу.
        Если у конкурента страница отвечает 404, товар автоматически скрывается с витрины (но синхронизация не отключается — вернётся сам, если страница снова появится).
      </p>

      <div className="mb-6 rounded-2xl border border-line bg-white p-4">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">Запустить синхронизацию сейчас</div>
        {bulkStatus?.running ? (
          <div>
            <div className="mb-2 text-sm text-ink">
              {bulkStatus.category ? `Категория: ${categories.find((c) => c.slug === bulkStatus.category)?.name || bulkStatus.category}` : "Все категории"}
              {" "}— {bulkStatus.done} / {bulkStatus.total}
              {bulkStatus.errors > 0 && <span className="text-danger"> · ошибок: {bulkStatus.errors}</span>}
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-paper">
              <div
                className="h-full bg-ink transition-all"
                style={{ width: `${bulkStatus.total ? Math.round((bulkStatus.done / bulkStatus.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={bulkCategory}
              onChange={(e) => setBulkCategory(e.target.value)}
              className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
            >
              <option value="">Все категории</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <button
              type="button"
              onClick={runBulk}
              className="rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-ink-soft"
            >
              Запустить
            </button>
            <span className="text-xs text-muted">Товары обновляются по одному с паузой — конкурента не сканируют пачкой сразу.</span>
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[["", "Все"], ["on", "Включена"], ["off", "Выключена"], ["error", "Ошибка"], ["hidden", "Скрыт"]].map(([v, label]) => (
            <button
              key={v || "all"}
              onClick={() => setFilter(v)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                filter === v ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
              }`}
            >
              {label}
            </button>
          ))}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink outline-none focus:border-ink"
          >
            <option value="">Все категории</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>
        <div className="text-xs text-muted">Всего: {total}</div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">
              <th className="p-4">
                <button type="button" onClick={() => toggleSort("name")} className="flex items-center gap-1.5 hover:text-ink">
                  Товар <SortIcon column="name" />
                </button>
              </th>
              <th className="p-4">Ссылка на конкурента</th>
              <th className="p-4">Цена от</th>
              <th className="p-4">
                <button type="button" onClick={() => toggleSort("last_sync")} className="flex items-center gap-1.5 hover:text-ink">
                  Последняя синхронизация <SortIcon column="last_sync" />
                </button>
              </th>
              <th className="p-4">Синк</th>
              <th className="p-4">Действие</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-line align-top">
                <td className="p-4">
                  <Link to={`/admin/products/${p.id}/edit`} className="font-semibold text-ink hover:underline">{p.name}</Link>
                  {p.is_active === false && (
                    <div className="mt-1 inline-block rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-danger">
                      Скрыт — нет у поставщика (404)
                    </div>
                  )}
                </td>
                <td className="p-4 max-w-[240px]">
                  <a href={p.import_source_url} target="_blank" rel="noreferrer" className="line-clamp-2 text-xs text-accent hover:underline">
                    {p.import_source_url}
                  </a>
                </td>
                <td className="p-4 font-bold text-ink">{formatPrice(p.min_price)}</td>
                <td className="p-4 text-xs">
                  {p.last_price_sync_at ? (
                    <>
                      <div className="text-muted">{new Date(p.last_price_sync_at).toLocaleString("ru-RU")}</div>
                      <div className={p.last_price_sync_status?.startsWith("error") ? "text-danger" : "text-success"}>
                        {p.last_price_sync_status?.startsWith("error") ? "Ошибка" : "OK"}
                      </div>
                    </>
                  ) : (
                    <span className="text-muted">ещё не запускалась</span>
                  )}
                </td>
                <td className="p-4">
                  <button
                    onClick={() => toggle(p.id, !p.price_sync_enabled)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      p.price_sync_enabled ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
                    }`}
                  >
                    {p.price_sync_enabled ? "Включена" : "Выключена"}
                  </button>
                </td>
                <td className="p-4">
                  <button
                    onClick={() => runNow(p.id)}
                    disabled={runningId === p.id}
                    className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-ink disabled:opacity-50"
                  >
                    {runningId === p.id ? "Запуск…" : "Сейчас"}
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-sm text-muted">Нет товаров со ссылкой на конкурента</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            ← Назад
          </button>
          <div className="px-3 text-sm font-semibold text-ink">{page} / {pages}</div>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
}

function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [flag, setFlag] = useState("");
  const [exportingExcel, setExportingExcel] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importUpdatingExisting, setImportUpdatingExisting] = useState(true);
  const [importingByUrl, setImportingByUrl] = useState(false);
  const importFileRef = useRef(null);

  const buildFilterParams = () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (category) params.category = category;
    if (brand) params.brand = brand;
    if (model.trim()) params.model = model.trim();
    if (flag === "featured") params.featured = true;
    if (flag === "new") params.new = true;
    return params;
  };

  const parseBooleanCell = (value) => {
    if (typeof value === "boolean") return value;
    const normalized = String(value ?? "").trim().toLowerCase();
    return ["1", "true", "yes", "y", "да"].includes(normalized);
  };

  const parseNumberCell = (value, fallback = 0) => {
    const normalized = String(value ?? "").replace(",", ".").trim();
    const num = Number(normalized);
    return Number.isFinite(num) ? num : fallback;
  };

  const parseImagesCell = (value) => {
    if (!value) return [];
    const raw = String(value).trim();
    if (!raw) return [];
    if (raw.startsWith("[")) {
      try {
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.map((x) => String(x || "").trim()).filter(Boolean) : [];
      } catch {
        return [];
      }
    }
    return raw.split(/[\n,;]/g).map((x) => x.trim()).filter(Boolean);
  };

  const parseSpecsCell = (value) => {
    if (!value) return {};
    const raw = String(value).trim();
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  };

  const load = async () => {
    const params = {
      page,
      page_size: 12,
      include_inactive: true,
    };
    Object.assign(params, buildFilterParams());

    const { data } = await api.get("/catalog/products", { params });
    setProducts(data.items || []);
    setPages(data.pages || 1);
    setTotal(data.total || 0);
  };

  const loadAllFilteredProducts = async () => {
    const params = {
      ...buildFilterParams(),
      limit: 5000,
      include_inactive: true,
    };
    const { data } = await api.get("/catalog/products", { params });
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportToExcel = async () => {
    setExportingExcel(true);
    try {
      const XLSX = await import("xlsx");
      const all = await loadAllFilteredProducts();
      if (!all.length) {
        toast.error("Нет товаров для выгрузки");
        return;
      }

      const rows = [];
      for (const product of all) {
        const base = {
          id: product.id,
          slug: product.slug,
          name: product.name,
          subtitle: product.subtitle || "",
          description: product.description || "",
          model: product.model || "",
          category_slug: product.category_slug || "",
          brand_slug: product.brand_slug || "",
          is_new: product.is_new ? 1 : 0,
          is_featured: product.is_featured ? 1 : 0,
          image_urls: JSON.stringify(product.images || []),
          specs_json: JSON.stringify(product.specs || {}),
          base_price: product.base_price ?? product.min_price ?? 0,
        };
        const vars = product.variations || [];
        if (!vars.length) {
          rows.push({
            ...base,
            variation_id: "",
            color: "",
            color_hex: "",
            variation_images_json: "[]",
            memory: "",
            region: "",
            warranty: "",
            price: base.base_price,
            old_price: "",
            stock: 0,
            sku: "",
          });
          continue;
        }
        for (const v of vars) {
          rows.push({
            ...base,
            variation_id: v.id || "",
            color: v.color || "",
            color_hex: v.color_hex || "",
            variation_images_json: JSON.stringify(v.images || (v.image ? [v.image] : [])),
            memory: v.memory || v.storage || "",
            region: v.region || "",
            warranty: v.warranty || "",
            price: v.price ?? 0,
            old_price: v.old_price ?? "",
            stock: v.stock ?? 0,
            sku: v.sku || "",
          });
        }
      }

      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "products");
      XLSX.writeFile(book, `products_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Выгружено строк: ${rows.length}`);
    } catch (e) {
      toast.error(e?.message || "Ошибка выгрузки Excel");
    } finally {
      setExportingExcel(false);
    }
  };

  const importFromExcel = async (file) => {
    if (!file) return;
    setImportingExcel(true);
    try {
      const XLSX = await import("xlsx");
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const firstSheetName = wb.SheetNames?.[0];
      if (!firstSheetName) throw new Error("Файл Excel пустой");
      const sheet = wb.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!rows.length) throw new Error("В файле нет строк для импорта");

      const grouped = new Map();
      for (const row of rows) {
        const slug = String(row.slug || "").trim();
        const name = String(row.name || "").trim();
        const key = slug || `name:${name}`;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(row);
      }

      const existingProducts = await api.get("/catalog/products", { params: { limit: 5000 } });
      const existingBySlug = new Map((existingProducts.data || []).map((p) => [p.slug, p]));

      let created = 0;
      let updated = 0;
      let failed = 0;

      for (const [, rowsGroup] of grouped) {
        try {
          const first = rowsGroup[0];
          const slug = String(first.slug || "").trim();
          const name = String(first.name || "").trim();
          if (!name) {
            failed += 1;
            continue;
          }

          const variations = rowsGroup
            .map((r) => ({
              id: String(r.variation_id || "").trim() || undefined,
              color: String(r.color || "").trim(),
              color_hex: String(r.color_hex || "").trim() || "#B7B7B7",
              images: parseImagesCell(r.variation_images_json || r.images || r.image),
              image: null,
              memory: String(r.memory || r.storage || "").trim() || null,
              region: String(r.region || "").trim() || null,
              warranty: String(r.warranty || "").trim() || null,
              price: parseNumberCell(r.price, 0),
              old_price: String(r.old_price || "").trim() ? parseNumberCell(r.old_price, 0) : null,
              stock: parseInt(String(r.stock || "0"), 10) || 0,
              sku: String(r.sku || "").trim(),
            }))
            .filter((v) => v.color || v.sku || v.price > 0);

          const finalVariations = variations.length
            ? variations
            : [{
              color: "Без цвета",
              color_hex: "#B7B7B7",
              images: [],
              image: null,
              memory: null,
              region: null,
              warranty: null,
              price: parseNumberCell(first.base_price, 0),
              old_price: null,
              stock: 0,
              sku: `${slug || makeSlug(name)}-1`,
            }];

          const minPrice = Math.min(...finalVariations.map((v) => Number(v.price) || 0));
          const payload = {
            slug,
            name,
            subtitle: String(first.subtitle || "").trim(),
            description: String(first.description || "").trim(),
            model: String(first.model || "").trim(),
            category_slug: String(first.category_slug || "").trim() || "iphone",
            brand_slug: String(first.brand_slug || "").trim() || "apple",
            images: parseImagesCell(first.image_urls),
            specs: parseSpecsCell(first.specs_json),
            variations: finalVariations,
            base_price: Number.isFinite(minPrice) ? minPrice : parseNumberCell(first.base_price, 0),
            is_new: parseBooleanCell(first.is_new),
            is_featured: parseBooleanCell(first.is_featured),
          };

          const existing = slug ? existingBySlug.get(slug) : null;
          if (existing?.id) {
            await api.patch(`/admin/products/${existing.id}`, payload);
            updated += 1;
          } else {
            await api.post("/admin/products", payload);
            created += 1;
          }
        } catch {
          failed += 1;
        }
      }

      await load();
      toast.success(`Импорт завершён: обновлено ${updated}, создано ${created}, ошибок ${failed}`);
    } catch (e) {
      toast.error(e?.message || "Ошибка импорта Excel");
    } finally {
      setImportingExcel(false);
      if (importFileRef.current) importFileRef.current.value = "";
    }
  };

  useEffect(() => {
    Promise.all([api.get("/catalog/categories"), api.get("/catalog/brands")]).then(([c, b]) => {
      setCategories(c.data || []);
      setBrands(b.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, category, brand, model, flag]);

  useEffect(() => {
    setPage(1);
  }, [q, category, brand, model, flag]);

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Удалить товар «${name}»?`)) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Товар удалён");
      load();
    } catch {
      toast.error("Не удалось удалить");
    }
  };

  const copyProduct = async (product) => {
    try {
      const payload = {
        slug: "",
        name: `${product.name} КОПИЯ`,
        subtitle: product.subtitle || "",
        description: product.description || "",
        model: product.model || "",
        category_slug: product.category_slug,
        brand_slug: product.brand_slug,
        images: product.images || [],
        specs: product.specs || {},
        variations: (product.variations || []).map((variation) => {
          const { id, ...rest } = variation;
          return rest;
        }),
        base_price: product.base_price ?? product.min_price ?? 0,
        is_new: !!product.is_new,
        is_featured: !!product.is_featured,
      };
      await api.post("/admin/products", payload);
      toast.success("Товар скопирован");
      load();
    } catch {
      toast.error("Не удалось скопировать товар");
    }
  };

  const importFromUrl = async () => {
    const url = importUrl.trim();
    if (!url) {
      toast.error("Вставьте ссылку на товар");
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      toast.error("Ссылка должна начинаться с http:// или https://");
      return;
    }
    setImportingByUrl(true);
    try {
      const { data } = await api.post("/admin/products/import-by-url", {
        url,
        update_existing: importUpdatingExisting,
      });
      const action = data?.action === "updated" ? "обновлен" : "создан";
      toast.success(`Товар ${action}: ${data?.product?.name || "без названия"}`);
      await load();
      setImportUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось импортировать товар по ссылке");
    } finally {
      setImportingByUrl(false);
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="text-sm text-muted">Найдено товаров: {total}</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="admin-export-products-excel"
            onClick={exportToExcel}
            disabled={exportingExcel || importingExcel}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-50"
          >
            {exportingExcel ? "Выгружаем..." : "Экспорт Excel"}
          </button>
          <button
            type="button"
            data-testid="admin-import-products-excel"
            onClick={() => importFileRef.current?.click()}
            disabled={importingExcel || exportingExcel}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-50"
          >
            {importingExcel ? "Загружаем..." : "Импорт Excel"}
          </button>
          <input
            ref={importFileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) importFromExcel(file);
            }}
          />
          <Link
            to="/admin/products/new"
            data-testid="admin-new-product"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-ink-soft"
          >
            <Plus size={14} /> Новый товар
          </Link>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-line bg-white p-4" data-testid="admin-import-by-url-block">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">Импорт по ссылке</div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
          <input
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://cordstore.ru/catalog/..."
            data-testid="admin-import-by-url-input"
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          />
          <button
            type="button"
            data-testid="admin-import-by-url-btn"
            onClick={importFromUrl}
            disabled={importingByUrl}
            className="inline-flex items-center justify-center rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-50"
          >
            {importingByUrl ? "Импортируем..." : "Импортировать"}
          </button>
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={importUpdatingExisting}
            onChange={(e) => setImportUpdatingExisting(e.target.checked)}
          />
          Обновлять существующий товар (по источнику ссылки/модели)
        </label>
      </div>

      <div className="mb-4 rounded-2xl border border-line bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setQ(searchDraft);
            }}
            placeholder="Поиск по названию"
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink md:col-span-2"
          />
          <input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Модель (например iPhone 17 Pro Max)"
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          >
            <option value="">Все категории</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          >
            <option value="">Все бренды</option>
            {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setQ(searchDraft)}
              className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
            >
              Найти
            </button>
            <button
              type="button"
              onClick={() => {
                setSearchDraft("");
                setQ("");
                setCategory("");
                setBrand("");
                setModel("");
                setFlag("");
              }}
              className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-muted transition-colors hover:text-ink"
            >
              Сброс
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {[['', 'Все'], ['featured', 'Хит продаж'], ['new', 'Новинки']].map(([value, label]) => (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => setFlag(value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                flag === value ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">
              <th className="p-4">Товар</th>
              <th className="p-4">Категория</th>
              <th className="p-4">Вариаций</th>
              <th className="p-4">Цена от</th>
              <th className="p-4">Остаток</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const totalStock = (p.variations || []).reduce((s, v) => s + (v.stock || 0), 0);
              const previewImage = p.images?.[0] || (p.variations || []).find((v) => (v.images || []).length > 0 || v.image)?.images?.[0] || (p.variations || []).find((v) => v.image)?.image;
              return (
                <tr key={p.id} className="border-b border-line" data-testid={`admin-product-${p.slug}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={resolveImageUrl(previewImage)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                      <div>
                        <div className="font-semibold text-ink">{p.name}</div>
                        <div className="text-xs text-muted">{p.slug}</div>
                        {p.model && <div className="text-xs text-muted">Модель: {p.model}</div>}
                        {p.is_active === false && (
                          <div className="mt-1 inline-block rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-danger">
                            Скрыт — нет у поставщика
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-ink">{categories.find((c) => c.slug === p.category_slug)?.name || p.category_slug}</td>
                  <td className="p-4 text-ink">{p.variations?.length || 0}</td>
                  <td className="p-4 font-bold text-ink">{formatPrice(p.min_price ?? p.base_price)}</td>
                  <td className="p-4"><span className={totalStock > 0 ? "text-success" : "text-danger"}>{totalStock}</span></td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => copyProduct(p)}
                        data-testid={`admin-copy-${p.slug}`}
                        className="grid h-8 w-8 place-items-center rounded-full border border-line text-ink transition-colors hover:bg-ink hover:text-white"
                        title="Скопировать"
                      >
                        <Copy size={13} />
                      </button>
                      <Link
                        to={`/admin/products/${p.id}/edit`}
                        data-testid={`admin-edit-${p.slug}`}
                        className="grid h-8 w-8 place-items-center rounded-full border border-line text-ink transition-colors hover:bg-ink hover:text-white"
                        title="Редактировать"
                      >
                        <Pencil size={13} />
                      </Link>
                      <button
                        onClick={() => deleteProduct(p.id, p.name)}
                        data-testid={`admin-delete-${p.slug}`}
                        className="grid h-8 w-8 place-items-center rounded-full border border-line text-ink transition-colors hover:bg-danger hover:text-white hover:border-danger"
                        title="Удалить"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {products.length === 0 && (
              <tr>
                <td colSpan={6} className="p-12 text-center text-sm text-muted">Товары не найдены</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2" data-testid="products-pagination">
          <button
            data-testid="products-page-prev"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            ← Назад
          </button>
          <div className="px-3 text-sm font-semibold text-ink">
            {page} / {pages}
          </div>
          <button
            data-testid="products-page-next"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
}

const EMPTY_VARIATION = {
  color: "",
  color_hex: DEFAULT_HEX,
  images: [],
  image: "",
  memory: "",
  region: "",
  warranty: "",
  price: 0,
  old_price: null,
  stock: 0,
  sku: "",
};

const EMPTY_PRODUCT = {
  slug: "",
  name: "",
  subtitle: "",
  description: "",
  model: "",
  category_slug: "iphone",
  brand_slug: "apple",
  images: [],
  specs: {},
  variations: [{ ...EMPTY_VARIATION }],
  base_price: 0,
  is_new: false,
  is_featured: false,
  avito_enabled: false,
};

const EMPTY_SETTINGS = {
  telegram_url: "",
  whatsapp_url: "",
  contact_email: "",
  contact_phone: "",
  pickup_address: "",
  working_hours_start: 10,
  working_hours_end: 20,
  payment_methods_enabled: { cash: true, sbp: false, card: false, split: true },
  legal_entity_name: "",
  legal_inn: "",
  legal_ogrn: "",
  legal_address: "",
  privacy_policy_text: "",
  consent_text: "",
  variation_configs: {
    iphone: {
      color: { label: "Цвет", enabled: true },
      color_hex: { label: "Hex", enabled: true },
      memory: { label: "Память", enabled: true },
      region: { label: "Связь", enabled: true },
      warranty: { label: "Гарантия", enabled: true },
    },
    mac: {
      color: { label: "Цвет", enabled: true },
      color_hex: { label: "Hex", enabled: true },
      case_color: { label: "Цвет корпуса", enabled: false },
      band_type: { label: "Тип ремешка", enabled: false },
      band_color: { label: "Цвет ремешка", enabled: false },
      memory: { label: "Накопитель", enabled: true },
      ram: { label: "ОЗУ", enabled: true },
      region: { label: "Регион", enabled: false },
      warranty: { label: "Гарантия", enabled: true },
    },
    ipad: {
      color: { label: "Цвет", enabled: true },
      color_hex: { label: "Hex", enabled: true },
      memory: { label: "Память", enabled: true },
      region: { label: "Связь", enabled: true },
      warranty: { label: "Гарантия", enabled: true },
    },
    airpods: {
      color: { label: "Цвет", enabled: true },
      color_hex: { label: "Hex", enabled: true },
      memory: { label: "Память", enabled: false },
      region: { label: "Связь", enabled: false },
      warranty: { label: "Гарантия", enabled: true },
    },
    accessories: {
      color: { label: "Цвет", enabled: true },
      color_hex: { label: "Hex", enabled: true },
      memory: { label: "Память", enabled: false },
      region: { label: "Связь", enabled: false },
      warranty: { label: "Гарантия", enabled: true },
    },
    watch: {
      color: { label: "Цвет ремешка", enabled: true },
      color_hex: { label: "Hex", enabled: true },
      case_color: { label: "Цвет корпуса", enabled: true },
      band_type: { label: "Тип ремешка", enabled: true },
      band_color: { label: "Доп. цвет ремешка", enabled: false },
      memory: { label: "Размер", enabled: true },
      region: { label: "Связь", enabled: false },
      warranty: { label: "Гарантия", enabled: true },
    },
  },
};

const VARIATION_FIELD_ORDER = ["color", "color_hex", "case_color", "band_type", "band_color", "memory", "ram", "region", "warranty", "price", "old_price", "stock", "sku"];

const VARIATION_FIELD_LABELS = {
  color: "Цвет",
  color_hex: "Hex",
  case_color: "Цвет корпуса",
  band_type: "Тип ремешка",
  band_color: "Цвет ремешка",
  memory: "Память",
  ram: "ОЗУ",
  region: "Связь",
  warranty: "Гарантия",
  price: "Цена ₽",
  old_price: "Старая цена",
  stock: "Остаток",
  sku: "SKU",
  is_preorder: "Предзаказ",
  preorder_note: "Текст предзаказа",
};

const VARIATION_FIELD_HELPERS = {
  color_hex: "Можно выбрать из палитры или вписать вручную, например #F5F5F7",
};

function getCategoryVariationConfig(settings, categorySlug) {
  const configs = settings?.variation_configs || EMPTY_SETTINGS.variation_configs;
  return configs[categorySlug] || configs.iphone || EMPTY_SETTINGS.variation_configs.iphone;
}

function ProductEditor() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!productId;
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVariationIndex, setUploadingVariationIndex] = useState(null);
  const [uploadingAvitoCoverIndex, setUploadingAvitoCoverIndex] = useState(null);
  const [specDraft, setSpecDraft] = useState({ key: "", value: "" });
  const [editingSpec, setEditingSpec] = useState(null);
  const [autoSlug, setAutoSlug] = useState(true);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);

  useEffect(() => {
    Promise.all([api.get("/catalog/categories"), api.get("/catalog/brands")]).then(([c, b]) => {
      setCategories(c.data);
      setBrands(b.data);
    });
  }, []);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const next = r.data || EMPTY_SETTINGS;
      setSettings({
        ...EMPTY_SETTINGS,
        ...next,
        variation_configs: {
          ...EMPTY_SETTINGS.variation_configs,
          ...(next.variation_configs || {}),
        },
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/products/${productId}`).then((r) => {
      const loaded = r.data || {};
      setForm({
        ...loaded,
        variations: (loaded.variations || []).map((v) => ({
          ...v,
          color_hex: normalizeHexColor(v.color_hex, DEFAULT_HEX),
          images: Array.isArray(v.images) ? v.images : (v.image ? [v.image] : []),
          image: Array.isArray(v.images) && v.images.length > 0 ? v.images[0] : (v.image || ""),
        })),
      });
      setAutoSlug(false);
    }).catch(() => {
      toast.error("Товар не найден");
      navigate("/admin/products");
    });
  }, [productId, isEdit, navigate]);

  useEffect(() => {
    if (!autoSlug) return;
    setForm((prev) => ({ ...prev, slug: makeSlug(prev.name) }));
  }, [form.name, autoSlug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVar = (i, k, v) => setForm((f) => ({
    ...f,
    variations: f.variations.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)),
  }));
  const addVar = () => set("variations", [...form.variations, { ...EMPTY_VARIATION }]);
  const rmVar = (i) => set("variations", form.variations.filter((_, idx) => idx !== i));
  const copyVar = (i) => {
    const source = form.variations[i];
    if (!source) return;
    const cloned = {
      ...source,
      id: undefined,
      images: Array.isArray(source.images) ? [...source.images] : [],
      image: Array.isArray(source.images) && source.images.length > 0 ? source.images[0] : (source.image || ""),
      sku: source.sku ? `${source.sku}-copy` : "",
    };
    set("variations", [
      ...form.variations.slice(0, i + 1),
      cloned,
      ...form.variations.slice(i + 1),
    ]);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("images", [...form.images, data.url]);
      toast.success("Изображение загружено");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось загрузить");
    } finally {
      setUploading(false);
    }
  };

  const setVariationImages = (index, nextImages) => setVar(index, "images", nextImages);

  const uploadVariationImages = async (index, files) => {
    const fileList = Array.from(files || []);
    if (!fileList.length) return;
    setUploadingVariationIndex(index);
    const fd = new FormData();
    try {
      const uploaded = [];
      for (const file of fileList) {
        fd.set("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setVariationImages(index, Array.from(new Set([...(form.variations[index]?.images || []), ...uploaded])));
      toast.success("Фото вариации загружены");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось загрузить");
    } finally {
      setUploadingVariationIndex(null);
    }
  };

  const uploadVariationAvitoCover = async (index, file) => {
    if (!file) return;
    setUploadingAvitoCoverIndex(index);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVar(index, "avito_cover_image", data.url);
      toast.success("Обложка для Avito загружена");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось загрузить");
    } finally {
      setUploadingAvitoCoverIndex(null);
    }
  };

  const removeVariationAvitoCover = (index) => setVar(index, "avito_cover_image", null);

  const addVariationImageUrl = (index, url) => {
    const clean = String(url || "").trim();
    if (!clean) return;
    setVariationImages(index, Array.from(new Set([...(form.variations[index]?.images || []), clean])));
  };

  const removeVariationImage = (index, imageIndex) => {
    setVariationImages(index, (form.variations[index]?.images || []).filter((_, idx) => idx !== imageIndex));
  };

  const activeVariationConfig = getCategoryVariationConfig(settings, form.category_slug);
  const visibleVariationFields = VARIATION_FIELD_ORDER.filter((fieldKey) => {
    if (["price", "old_price", "stock", "sku"].includes(fieldKey)) return true;
    return activeVariationConfig[fieldKey]?.enabled === true;
  });

  const rmImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  const addSpec = () => {
    if (!specDraft.key.trim()) return;
    set("specs", { ...form.specs, [specDraft.key.trim()]: specDraft.value });
    setSpecDraft({ key: "", value: "" });
  };
  const rmSpec = (key) => {
    const s = { ...form.specs };
    delete s[key];
    set("specs", s);
    if (editingSpec?.originalKey === key) {
      setEditingSpec(null);
    }
  };

  const beginEditSpec = (key, value) => {
    setEditingSpec({ originalKey: key, key, value: value || "" });
  };

  const cancelEditSpec = () => {
    setEditingSpec(null);
  };

  const saveEditedSpec = () => {
    if (!editingSpec) return;
    const nextKey = (editingSpec.key || "").trim();
    if (!nextKey) {
      toast.error("Название характеристики не может быть пустым");
      return;
    }
    const currentSpecs = { ...(form.specs || {}) };
    if (nextKey !== editingSpec.originalKey && Object.prototype.hasOwnProperty.call(currentSpecs, nextKey)) {
      toast.error("Характеристика с таким названием уже существует");
      return;
    }
    delete currentSpecs[editingSpec.originalKey];
    currentSpecs[nextKey] = editingSpec.value || "";
    set("specs", currentSpecs);
    setEditingSpec(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const normalizedVariations = form.variations.map((v) => ({
        ...v,
        color_hex: normalizeHexColor(v.color_hex, DEFAULT_HEX),
        images: Array.from(new Set((v.images || []).map((src) => String(src || "").trim()).filter(Boolean))),
        image: (v.images || []).map((src) => String(src || "").trim()).filter(Boolean)[0] || v.image || null,
        price: Number(v.price) || 0,
        old_price: v.old_price ? Number(v.old_price) : null,
        stock: Number(v.stock) || 0,
      }));
      const positiveVariationPrices = normalizedVariations
        .map((v) => Number(v.price) || 0)
        .filter((p) => p > 0);
      const computedBasePrice = positiveVariationPrices.length
        ? Math.min(...positiveVariationPrices)
        : (Number(form.base_price) || 0);
      const payload = {
        ...form,
        slug: autoSlug ? "" : form.slug.trim(),
        base_price: computedBasePrice,
        variations: normalizedVariations,
      };
      if (isEdit) {
        await api.patch(`/admin/products/${productId}`, payload);
        toast.success("Товар обновлён");
      } else {
        if (!payload.name) throw new Error("name обязателен");
        await api.post("/admin/products", payload);
        toast.success("Товар создан");
      }
      navigate("/admin/products");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-product-editor" className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/admin/products" className="inline-flex items-center gap-2 text-sm font-semibold text-muted hover:text-ink">
          <ArrowLeft size={14} /> К списку товаров
        </Link>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-save-product"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Сохраняем…" : (isEdit ? "Сохранить" : "Создать")}
        </button>
      </div>

      <div className="rounded-2xl border border-line bg-white p-6">
        <h3 className="mb-4 text-lg font-bold text-ink">Основное</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block md:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted">Slug (уникальный)</span>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
              <input
                data-testid="editor-slug"
                value={form.slug}
                onChange={(e) => {
                  setAutoSlug(false);
                  set("slug", makeSlug(e.target.value));
                }}
                placeholder="Будет сгенерирован автоматически, если оставить пустым"
                className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
              />
              <button
                type="button"
                data-testid="editor-generate-slug"
                onClick={() => {
                  setAutoSlug(true);
                  set("slug", makeSlug(form.name));
                }}
                className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
              >
                Сгенерировать
              </button>
            </div>
            <label className="mt-2 inline-flex items-center gap-2 text-xs text-muted">
              <input
                type="checkbox"
                checked={autoSlug}
                onChange={(e) => setAutoSlug(e.target.checked)}
              />
              Авто из названия
            </label>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Название</span>
            <input data-testid="editor-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink" />
          </label>
          <label className="block md:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted">Подзаголовок</span>
            <input data-testid="editor-subtitle" value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink" />
          </label>
          <label className="block md:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted">Модель</span>
            <input
              data-testid="editor-model"
              value={form.model || ""}
              onChange={(e) => set("model", e.target.value)}
              placeholder="Например: iPhone 17 Pro Max"
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted">Описание</span>
            <textarea data-testid="editor-description" rows={4} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Категория</span>
            <select data-testid="editor-category" value={form.category_slug} onChange={(e) => set("category_slug", e.target.value)} className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Бренд</span>
            <select data-testid="editor-brand" value={form.brand_slug} onChange={(e) => set("brand_slug", e.target.value)} className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink">
              {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
          </label>
          <div className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Базовая цена</span>
            <div className="rounded-lg border border-line bg-paper px-4 py-2 text-sm text-ink">
              Считается автоматически из минимальной цены вариаций
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="editor-is-new" checked={!!form.is_new} onChange={(e) => set("is_new", e.target.checked)} />
              Новинка
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="editor-is-featured" checked={!!form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} />
              Хит продаж
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="editor-avito-enabled" checked={!!form.avito_enabled} onChange={(e) => set("avito_enabled", e.target.checked)} />
              Показывать на Avito
            </label>
          </div>
        </div>
      </div>

      {/* Images */}
      <div className="rounded-2xl border border-line bg-white p-6">
        <h3 className="mb-4 text-lg font-bold text-ink">Изображения</h3>
        <div className="mb-4 flex flex-wrap gap-3">
          {form.images.map((src, i) => (
            <div key={i} className="group relative h-24 w-24 overflow-hidden rounded-xl border border-line bg-paper">
              <img src={resolveImageUrl(src)} alt="" className="h-full w-full object-cover" />
              <button
                onClick={() => rmImage(i)}
                data-testid={`editor-remove-image-${i}`}
                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white/90 text-danger opacity-0 transition-opacity group-hover:opacity-100"
                type="button"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <label className="grid h-24 w-24 cursor-pointer place-items-center rounded-xl border-2 border-dashed border-line bg-paper text-muted transition-colors hover:border-ink hover:text-ink">
            <input
              type="file"
              accept="image/*"
              data-testid="editor-upload"
              onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
              className="hidden"
            />
            {uploading ? "…" : <Upload size={18} />}
          </label>
        </div>
        <p className="text-xs text-muted">Форматы: jpg, png, webp, gif · до 8 МБ. Первое изображение — главное.</p>
      </div>

      {/* Variations */}
      <div className="rounded-2xl border border-line bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-ink">Вариации ({form.variations.length})</h3>
          <button
            type="button"
            onClick={addVar}
            data-testid="editor-add-variation"
            className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
          >
            <Plus size={12} /> Добавить
          </button>
        </div>
        <div className="space-y-3">
          {form.variations.map((v, i) => (
            <div key={i} data-testid={`editor-variation-${i}`} className="grid grid-cols-2 gap-3 rounded-xl border border-line bg-paper p-4 md:grid-cols-8">
              <div className="md:col-span-8 flex items-center justify-between gap-3">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-soft">Вариация #{i + 1}</div>
                <button
                  type="button"
                  onClick={() => copyVar(i)}
                  data-testid={`editor-copy-variation-${i}`}
                  className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-3 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
                >
                  <Copy size={12} /> Скопировать
                </button>
              </div>
              {visibleVariationFields.includes("color") && (
                <label className="md:col-span-2">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.color?.label || VARIATION_FIELD_LABELS.color}</span>
                  <input value={v.color} onChange={(e) => setVar(i, "color", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              {visibleVariationFields.includes("color_hex") && (
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.color_hex?.label || VARIATION_FIELD_LABELS.color_hex}</span>
                  <div className="grid grid-cols-[52px_1fr] gap-2">
                    <input
                      type="color"
                      value={normalizeHexColor(v.color_hex, DEFAULT_HEX)}
                      onChange={(e) => setVar(i, "color_hex", e.target.value)}
                      className="h-9 w-full rounded-lg border border-line bg-white"
                      title="Выбрать цвет из палитры"
                    />
                    <input
                      value={v.color_hex || ""}
                      onChange={(e) => setVar(i, "color_hex", e.target.value)}
                      onBlur={(e) => setVar(i, "color_hex", normalizeHexColor(e.target.value, DEFAULT_HEX))}
                      placeholder="#F5F5F7"
                      className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink"
                    />
                  </div>
                  {VARIATION_FIELD_HELPERS.color_hex && <div className="mt-1 text-[10px] text-muted-soft">{VARIATION_FIELD_HELPERS.color_hex}</div>}
                </label>
              )}
              {visibleVariationFields.includes("memory") && (
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.memory?.label || VARIATION_FIELD_LABELS.memory}</span>
                  <input value={v.memory || v.storage || ""} onChange={(e) => setVar(i, "memory", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              {visibleVariationFields.includes("case_color") && (
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.case_color?.label || VARIATION_FIELD_LABELS.case_color}</span>
                  <input value={v.case_color || ""} onChange={(e) => setVar(i, "case_color", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              {visibleVariationFields.includes("band_type") && (
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.band_type?.label || VARIATION_FIELD_LABELS.band_type}</span>
                  <input value={v.band_type || ""} onChange={(e) => setVar(i, "band_type", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              {visibleVariationFields.includes("band_color") && (
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.band_color?.label || VARIATION_FIELD_LABELS.band_color}</span>
                  <input value={v.band_color || ""} onChange={(e) => setVar(i, "band_color", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              {visibleVariationFields.includes("region") && (
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.region?.label || VARIATION_FIELD_LABELS.region}</span>
                  <input value={v.region || ""} onChange={(e) => setVar(i, "region", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              {visibleVariationFields.includes("warranty") && (
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{activeVariationConfig.warranty?.label || VARIATION_FIELD_LABELS.warranty}</span>
                  <input value={v.warranty || ""} onChange={(e) => setVar(i, "warranty", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              <label>
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{VARIATION_FIELD_LABELS.price}</span>
                <input type="number" value={v.price} onChange={(e) => setVar(i, "price", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
              </label>
              <label>
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{VARIATION_FIELD_LABELS.stock}</span>
                <input type="number" value={v.stock} onChange={(e) => setVar(i, "stock", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
              </label>
              <label className="md:col-span-2">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{VARIATION_FIELD_LABELS.sku}</span>
                <input value={v.sku} onChange={(e) => setVar(i, "sku", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
              </label>
              <label>
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{VARIATION_FIELD_LABELS.old_price}</span>
                <input type="number" value={v.old_price || ""} onChange={(e) => setVar(i, "old_price", e.target.value)} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
              </label>
              <label className="flex items-center gap-2 pt-5">
                <input type="checkbox" checked={!!v.is_preorder} onChange={(e) => setVar(i, "is_preorder", e.target.checked)} className="h-4 w-4 rounded border-line" />
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{VARIATION_FIELD_LABELS.is_preorder} (доступно при остатке 0)</span>
              </label>
              {v.is_preorder && (
                <label className="md:col-span-2">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">{VARIATION_FIELD_LABELS.preorder_note}</span>
                  <input value={v.preorder_note || ""} onChange={(e) => setVar(i, "preorder_note", e.target.value)} placeholder="Например: В наличии с 19 сентября" className="w-full rounded-lg border border-line bg-white px-3 py-2 text-xs outline-none focus:border-ink" />
                </label>
              )}
              <div className="md:col-span-8 space-y-3 rounded-xl border border-dashed border-line bg-white p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">Фото вариации</span>
                    <div className="text-xs text-muted">Можно загрузить несколько файлов сразу. Эти фото будут показаны на странице вариации.</div>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-line bg-white px-3 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      data-testid={`editor-upload-variation-${i}`}
                      onChange={(e) => e.target.files?.length && uploadVariationImages(i, e.target.files)}
                      className="hidden"
                    />
                    {uploadingVariationIndex === i ? "Загрузка…" : "Загрузить фото"}
                  </label>
                </div>
                {Array.isArray(v.images) && v.images.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {v.images.map((src, imageIndex) => (
                      <div key={`${src}-${imageIndex}`} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-line bg-paper">
                        <img src={resolveImageUrl(src)} alt="" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeVariationImage(i, imageIndex)}
                          className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white/90 text-danger opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-line bg-paper px-3 py-2 text-xs text-muted">Фото не добавлены</div>
                )}
              </div>
              <div className="md:col-span-8 space-y-3 rounded-xl border border-dashed border-line bg-white p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-soft">Обложка для Avito</span>
                    <div className="text-xs text-muted">
                      Отдельное первое фото только для объявления на Avito (не с сайта). Показывается перед обычными фото. JPEG или PNG.
                    </div>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-line bg-white px-3 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white">
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      data-testid={`editor-upload-avito-cover-${i}`}
                      onChange={(e) => e.target.files?.[0] && uploadVariationAvitoCover(i, e.target.files[0])}
                      className="hidden"
                    />
                    {uploadingAvitoCoverIndex === i ? "Загрузка…" : v.avito_cover_image ? "Заменить фото" : "Загрузить фото"}
                  </label>
                </div>
                {v.avito_cover_image ? (
                  <div className="group relative h-20 w-20 overflow-hidden rounded-xl border border-line bg-paper">
                    <img src={resolveImageUrl(v.avito_cover_image)} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeVariationAvitoCover(i)}
                      data-testid={`editor-remove-avito-cover-${i}`}
                      className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white/90 text-danger opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-line bg-paper px-3 py-2 text-xs text-muted">Не задана — используются обычные фото</div>
                )}
              </div>
              <div className="flex items-end md:col-span-8 md:justify-end">
                <button
                  type="button"
                  onClick={() => rmVar(i)}
                  data-testid={`editor-remove-variation-${i}`}
                  className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-3 py-2 text-xs font-semibold text-danger transition-colors hover:bg-danger hover:text-white hover:border-danger"
                >
                  <Trash2 size={12} /> Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Specs */}
      <div className="rounded-2xl border border-line bg-white p-6">
        <h3 className="mb-4 text-lg font-bold text-ink">Характеристики</h3>
        {Object.keys(form.specs || {}).length > 0 && (
          <div className="mb-4 divide-y divide-line rounded-xl border border-line">
            {Object.entries(form.specs).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[1fr_2fr_auto] gap-3 px-4 py-3 text-sm">
                {editingSpec?.originalKey === k ? (
                  <>
                    <input
                      value={editingSpec.key}
                      onChange={(e) => setEditingSpec((prev) => ({ ...prev, key: e.target.value }))}
                      className="rounded-lg border border-line bg-white px-3 py-2 text-sm font-semibold text-muted outline-none focus:border-ink"
                    />
                    <input
                      value={editingSpec.value}
                      onChange={(e) => setEditingSpec((prev) => ({ ...prev, value: e.target.value }))}
                      className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ink"
                    />
                    <div className="flex items-center gap-2 justify-end">
                      <button type="button" onClick={saveEditedSpec} className="rounded-full border border-line bg-white px-3 py-1 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white">
                        Сохранить
                      </button>
                      <button type="button" onClick={cancelEditSpec} className="rounded-full border border-line bg-white px-3 py-1 text-xs font-semibold text-muted transition-colors hover:text-ink">
                        Отмена
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-muted">{k}</div>
                    <div className="text-ink">{v}</div>
                    <div className="flex items-center gap-2 justify-end">
                      <button type="button" onClick={() => beginEditSpec(k, v)} className="text-ink hover:underline">
                        <Pencil size={13} />
                      </button>
                      <button type="button" onClick={() => rmSpec(k)} className="text-danger hover:underline">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-[1fr_2fr_auto] gap-3">
          <input
            placeholder="Название (например, Дисплей)"
            data-testid="editor-spec-key"
            value={specDraft.key}
            onChange={(e) => setSpecDraft((s) => ({ ...s, key: e.target.value }))}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          />
          <input
            placeholder="Значение"
            data-testid="editor-spec-value"
            value={specDraft.value}
            onChange={(e) => setSpecDraft((s) => ({ ...s, value: e.target.value }))}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          />
          <button
            type="button"
            onClick={addSpec}
            data-testid="editor-spec-add"
            className="inline-flex items-center gap-1 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-ink-soft"
          >
            <Plus size={12} /> Добавить
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsAdmin() {
  const [form, setForm] = useState(EMPTY_SETTINGS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm({
      telegram_url: r.data.telegram_url || "",
      whatsapp_url: r.data.whatsapp_url || "",
      contact_email: r.data.contact_email || "",
      contact_phone: r.data.contact_phone || "",
      pickup_address: r.data.pickup_address || "",
      working_hours_start: r.data.working_hours_start ?? 10,
      working_hours_end: r.data.working_hours_end ?? 20,
      payment_methods_enabled: {
        ...EMPTY_SETTINGS.payment_methods_enabled,
        ...(r.data.payment_methods_enabled || {}),
      },
      legal_entity_name: r.data.legal_entity_name || "",
      legal_inn: r.data.legal_inn || "",
      legal_ogrn: r.data.legal_ogrn || "",
      legal_address: r.data.legal_address || "",
      privacy_policy_text: r.data.privacy_policy_text || "",
      consent_text: r.data.consent_text || "",
      variation_configs: {
        ...EMPTY_SETTINGS.variation_configs,
        ...(r.data.variation_configs || {}),
      },
    })).catch(() => {
      toast.error("Не удалось загрузить настройки");
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", form);
      setForm({
        telegram_url: data.telegram_url || "",
        whatsapp_url: data.whatsapp_url || "",
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
        pickup_address: data.pickup_address || "",
        working_hours_start: data.working_hours_start ?? 10,
        working_hours_end: data.working_hours_end ?? 20,
        payment_methods_enabled: {
          ...EMPTY_SETTINGS.payment_methods_enabled,
          ...(data.payment_methods_enabled || {}),
        },
        legal_entity_name: data.legal_entity_name || "",
        legal_inn: data.legal_inn || "",
        legal_ogrn: data.legal_ogrn || "",
        legal_address: data.legal_address || "",
        privacy_policy_text: data.privacy_policy_text || "",
        consent_text: data.consent_text || "",
        variation_configs: {
          ...EMPTY_SETTINGS.variation_configs,
          ...(data.variation_configs || {}),
        },
      });
      toast.success("Настройки обновлены");
    } catch {
      toast.error("Не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="rounded-2xl border border-line bg-white p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-ink">Настройки магазина и вариаций</h3>
          <p className="mt-1 text-sm text-muted">Ссылки контактов.</p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Сохраняем…" : "Сохранить"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">Telegram URL</span>
          <input
            data-testid="settings-telegram-url"
            value={form.telegram_url}
            onChange={(e) => setForm((prev) => ({ ...prev, telegram_url: e.target.value }))}
            placeholder="https://t.me/your_account"
            className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">WhatsApp URL</span>
          <input
            data-testid="settings-whatsapp-url"
            value={form.whatsapp_url}
            onChange={(e) => setForm((prev) => ({ ...prev, whatsapp_url: e.target.value }))}
            placeholder="https://wa.me/79990000000"
            className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">Email для связи (футер)</span>
          <input
            data-testid="settings-contact-email"
            value={form.contact_email}
            onChange={(e) => setForm((prev) => ({ ...prev, contact_email: e.target.value }))}
            placeholder="hello@vip77.store"
            className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">Телефон для связи (футер)</span>
          <input
            data-testid="settings-contact-phone"
            value={form.contact_phone}
            onChange={(e) => setForm((prev) => ({ ...prev, contact_phone: e.target.value }))}
            placeholder="+7 (499) 777-77-77"
            className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">Адрес самовывоза (в оформлении заказа)</span>
          <input
            data-testid="settings-pickup-address"
            value={form.pickup_address}
            onChange={(e) => setForm((prev) => ({ ...prev, pickup_address: e.target.value }))}
            className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">Часы работы (МСК)</span>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={23}
              data-testid="settings-working-hours-start"
              value={form.working_hours_start}
              onChange={(e) => setForm((prev) => ({ ...prev, working_hours_start: Number(e.target.value) }))}
              className="w-20 rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
            />
            <span className="text-sm text-muted">до</span>
            <input
              type="number"
              min={1}
              max={24}
              data-testid="settings-working-hours-end"
              value={form.working_hours_end}
              onChange={(e) => setForm((prev) => ({ ...prev, working_hours_end: Number(e.target.value) }))}
              className="w-20 rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
            />
          </div>
          <p className="mt-1 text-xs text-muted">
            Вне этих часов на оформлении заказа и в письме клиенту показывается предупреждение о нерабочем времени.
          </p>
        </label>
      </div>

      <div className="mt-8 border-t border-line pt-6">
        <h4 className="text-sm font-bold uppercase tracking-[0.16em] text-muted-soft">Способы оплаты</h4>
        <p className="mt-1 text-xs text-muted">Выключенный способ помечается на оформлении заказа как «Скоро будет доступно» и недоступен для выбора.</p>
        <div className="mt-4 flex flex-wrap gap-4">
          {[
            { key: "cash", label: "Наличный расчёт" },
            { key: "sbp", label: "Оплата по СБП" },
            { key: "card", label: "Банковской картой" },
            { key: "split", label: "Яндекс Сплит" },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={`settings-payment-${key}`}
                checked={!!form.payment_methods_enabled[key]}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  payment_methods_enabled: { ...prev.payment_methods_enabled, [key]: e.target.checked },
                }))}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-8 border-t border-line pt-6">
        <h4 className="text-sm font-bold uppercase tracking-[0.16em] text-muted-soft">Реквизиты</h4>
        <p className="mt-1 text-xs text-muted">Отображаются на публичной странице /requisites.</p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Полное наименование</span>
            <input
              data-testid="settings-legal-name"
              value={form.legal_entity_name}
              onChange={(e) => setForm((prev) => ({ ...prev, legal_entity_name: e.target.value }))}
              placeholder="ИП Иванов Иван Иванович"
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">ИНН</span>
            <input
              data-testid="settings-legal-inn"
              value={form.legal_inn}
              onChange={(e) => setForm((prev) => ({ ...prev, legal_inn: e.target.value }))}
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">ОГРН / ОГРНИП</span>
            <input
              data-testid="settings-legal-ogrn"
              value={form.legal_ogrn}
              onChange={(e) => setForm((prev) => ({ ...prev, legal_ogrn: e.target.value }))}
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Юридический адрес</span>
            <input
              data-testid="settings-legal-address"
              value={form.legal_address}
              onChange={(e) => setForm((prev) => ({ ...prev, legal_address: e.target.value }))}
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
        </div>
      </div>

      <div className="mt-8 border-t border-line pt-6">
        <h4 className="text-sm font-bold uppercase tracking-[0.16em] text-muted-soft">Правовые документы</h4>
        <p className="mt-1 text-xs text-muted">Текст публичных страниц /privacy-policy и /consent. Разделяйте абзацы пустой строкой.</p>
        <div className="mt-4 grid grid-cols-1 gap-4">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Политика обработки персональных данных</span>
            <textarea
              data-testid="settings-privacy-policy"
              value={form.privacy_policy_text}
              onChange={(e) => setForm((prev) => ({ ...prev, privacy_policy_text: e.target.value }))}
              rows={10}
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Согласие на обработку персональных данных</span>
            <textarea
              data-testid="settings-consent-text"
              value={form.consent_text}
              onChange={(e) => setForm((prev) => ({ ...prev, consent_text: e.target.value }))}
              rows={10}
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [productSlug, setProductSlug] = useState("");

  const load = async () => {
    const params = { page, page_size: 12 };
    if (query) params.q = query;
    if (productSlug) params.product_slug = productSlug;
    const { data } = await api.get("/admin/reviews", { params });
    setReviews(data.items || []);
    setPages(data.pages || 1);
    setTotal(data.total || 0);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, query, productSlug]);
  useEffect(() => { setPage(1); }, [query, productSlug]);

  const removeReview = async (id) => {
    if (!window.confirm("Удалить отзыв?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Отзыв удалён");
      load();
    } catch {
      toast.error("Не удалось удалить отзыв");
    }
  };

  return (
    <div data-testid="admin-reviews" className="space-y-6">
      <div className="rounded-2xl border border-line bg-white p-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-ink">Отзывы</h3>
            <p className="mt-1 text-sm text-muted">Новые отзывы сверху, можно искать и удалять.</p>
          </div>
          <div className="text-sm text-muted">Всего: {total}</div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Поиск</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Текст, автор или slug товара"
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted">Slug товара</span>
            <input
              value={productSlug}
              onChange={(e) => setProductSlug(e.target.value)}
              placeholder="iphone-17-pro-max"
              className="w-full rounded-lg border border-line bg-white px-4 py-2 text-sm outline-none focus:border-ink"
            />
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-line bg-white">
        <table className="w-full min-w-[920px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">
              <th className="p-4">Товар</th>
              <th className="p-4">Автор</th>
              <th className="p-4">Оценка</th>
              <th className="p-4">Отзыв</th>
              <th className="p-4">Дата</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => (
              <tr key={review.id} className="border-b border-line align-top">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={resolveImageUrl(review.product?.image)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                    <div>
                      <div className="font-semibold text-ink">{review.product?.name || review.product_slug}</div>
                      <div className="text-xs text-muted">{review.product?.slug || review.product_slug}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="font-semibold text-ink">{review.user_name}</div>
                  <div className="text-xs text-muted">{review.user_id}</div>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-1 text-warning">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <Star key={value} size={14} fill={value <= review.rating ? "currentColor" : "none"} />
                    ))}
                  </div>
                </td>
                <td className="p-4 max-w-[420px]">
                  <p className="line-clamp-3 text-ink">{review.text}</p>
                </td>
                <td className="p-4 text-xs text-muted">{new Date(review.updated_at || review.created_at).toLocaleString("ru-RU")}</td>
                <td className="p-4">
                  <button
                    onClick={() => removeReview(review.id)}
                    className="grid h-8 w-8 place-items-center rounded-full border border-line text-ink transition-colors hover:border-danger hover:bg-danger hover:text-white"
                    title="Удалить отзыв"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {reviews.length === 0 && (
              <tr>
                <td colSpan={6} className="p-12 text-center text-sm text-muted">Отзывов пока нет</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            ← Назад
          </button>
          <div className="px-3 text-sm font-semibold text-ink">{page} / {pages}</div>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Админ-панель</div>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tightest text-ink md:text-5xl">Управление магазином</h1>
      </div>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <nav className="flex flex-nowrap gap-1 overflow-x-auto rounded-2xl border border-line bg-white p-2 lg:flex-col lg:flex-wrap lg:overflow-visible">
            <NavLink end to="/admin" data-testid="admin-tab-dashboard" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <LayoutDashboard size={15} /> Дашборд
            </NavLink>
            <NavLink to="/admin/orders" data-testid="admin-tab-orders" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <ShoppingBag size={15} /> Заказы
            </NavLink>
            <NavLink to="/admin/quick-orders" data-testid="admin-tab-quick-orders" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <ShoppingBag size={15} /> Быстрые заявки
            </NavLink>
            <NavLink to="/admin/price-sync" data-testid="admin-tab-price-sync" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <ShoppingBag size={15} /> Синхронизация цен
            </NavLink>
            <NavLink to="/admin/users" data-testid="admin-tab-users" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <Users size={15} /> Пользователи
            </NavLink>
            <NavLink to="/admin/products" data-testid="admin-tab-products" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <Package size={15} /> Товары
            </NavLink>
            <NavLink to="/admin/reviews" data-testid="admin-tab-reviews" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <MessageCircle size={15} /> Отзывы
            </NavLink>
            <NavLink to="/admin/settings" data-testid="admin-tab-settings" className={({ isActive }) => `flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"}`}>
              <Save size={15} /> Настройки
            </NavLink>
          </nav>
        </aside>
        <div className="lg:col-span-9">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="orders" element={<OrdersAdmin />} />
            <Route path="quick-orders" element={<QuickOrdersAdmin />} />
            <Route path="price-sync" element={<PriceSyncAdmin />} />
            <Route path="users" element={<UsersAdmin />} />
            <Route path="products" element={<ProductsAdmin />} />
            <Route path="products/new" element={<ProductEditor />} />
            <Route path="products/:productId/edit" element={<ProductEditor />} />
            <Route path="reviews" element={<ReviewsAdmin />} />
            <Route path="settings" element={<SettingsAdmin />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
